/**
 * Kampagne-konstanter delt mellem server og web ([D-261], session 31).
 *
 * Manuelt annonceforbrug pr. kampagne: faste kategorier for offline/ikke-API-
 * kanaler (Charlies liste 31-08-2026), så subtotaler kan grupperes stabilt.
 * Beløb er ALTID ekskl. moms. 'andet' er ventilen — beskrivelsen bærer detaljen
 * (fx "ALT Interiør, Aug 2026 udgave, bagside, indretningskonsulent").
 */

export const MANUAL_SPEND_CATEGORIES = [
  'stillads_gavl',
  'bus_trailer',
  'tv_streaming',
  'radio',
  'magasiner_aviser',
  'print',
  'outdoor',
  'andet',
] as const;

export type ManualSpendCategory = (typeof MANUAL_SPEND_CATEGORIES)[number];

export const MANUAL_SPEND_CATEGORY_LABELS: Record<ManualSpendCategory, string> = {
  stillads_gavl: 'Stillads og gavl',
  bus_trailer: 'Bus og trailer',
  tv_streaming: 'TV og streamingtjenester',
  radio: 'Radio',
  magasiner_aviser: 'Magasiner og aviser',
  print: 'Print',
  outdoor: 'Outdoor',
  andet: 'Andet',
};

// ---------------------------------------------------------------------------
// Kampagnens LIVSFASE ([D-304], Charlie 18-09-2026)
//
// Kun BESLUTNINGEN er manuel og gemmes i `campaigns.status`: et udkast bliver
// enten godkendt eller afvist. Resten af forløbet afledes af kampagneperioden
// ved HVER læsning og gemmes aldrig, så fasen ikke kan stå forkert i databasen:
//
//   udkast ──(manuelt)──> afvist
//   udkast ──(manuelt)──> godkendt ──> planlagt ──> aktiv ──> afsluttet
//                          (ingen       (start i     (i perio-  (slutdato
//                           periode)     fremtiden)   den)       passeret)
//
// "Godkendt" er altså en godkendt kampagne UDEN fastlagt periode; i det øjeblik
// startdatoen sættes, flytter den selv til Planlagte. En løbende kampagne
// (ingen slutdato, [D-264]) bliver ved med at være aktiv.
//
// De gamle værdier 'active' og 'archived' læses som godkendt, så koden tåler en
// database der endnu ikke er migreret (`migrate:campaign-lifecycle`).
// ---------------------------------------------------------------------------

/** Den gemte, manuelle beslutning. */
export const CAMPAIGN_DECISIONS = ['draft', 'approved', 'rejected'] as const;
export type CampaignDecision = (typeof CAMPAIGN_DECISIONS)[number];

/** Fasen som brugeren ser den. Afledt, aldrig gemt. */
export const CAMPAIGN_LIFECYCLES = ['draft', 'rejected', 'approved', 'planned', 'active', 'ended'] as const;
export type CampaignLifecycle = (typeof CAMPAIGN_LIFECYCLES)[number];

export const CAMPAIGN_LIFECYCLE_LABELS: Record<CampaignLifecycle, string> = {
  draft: 'Udkast',
  rejected: 'Afvist',
  approved: 'Godkendt',
  planned: 'Planlagt',
  active: 'Aktiv',
  ended: 'Afsluttet',
};

/** Normaliserer den gemte status: de gamle 'active'/'archived' er godkendte. */
export function campaignDecision(status: string | null | undefined): CampaignDecision {
  if (status === 'draft' || status === 'rejected') return status;
  return 'approved';
}

/**
 * Kampagnens fase på dagen `today` (YYYY-MM-DD, Europe/Copenhagen hos kalderen).
 * Datoerne er inklusive i begge ender, præcis som målingen regner dem.
 */
export function campaignLifecycle(
  status: string | null | undefined,
  periodStart: string | null | undefined,
  periodEnd: string | null | undefined,
  today: string,
): CampaignLifecycle {
  const decision = campaignDecision(status);
  if (decision !== 'approved') return decision;
  if (!periodStart) return 'approved';
  if (today < periodStart) return 'planned';
  if (periodEnd != null && periodEnd !== '' && today > periodEnd) return 'ended';
  return 'active';
}

/**
 * De 5 undermenuer under Marketing → Kampagner. Afviste kampagner har ingen
 * egen menu: de står nederst på Udkast, fordi en afvisning også er en
 * beslutning der skal kunne findes igen.
 */
export const CAMPAIGN_MENU = [
  { slug: 'drafts', lifecycle: 'draft', label: 'Udkast' },
  { slug: 'approved', lifecycle: 'approved', label: 'Godkendte' },
  { slug: 'planned', lifecycle: 'planned', label: 'Planlagte' },
  { slug: 'active', lifecycle: 'active', label: 'Aktive' },
  { slug: 'ended', lifecycle: 'ended', label: 'Afsluttede' },
] as const satisfies ReadonlyArray<{ slug: string; lifecycle: CampaignLifecycle; label: string }>;

export const CAMPAIGNS_BASE_PATH = '/marketing/campaigns';

/** Undermenuens sti for en fase. Afviste hører til på Udkast. */
export function campaignMenuPath(lifecycle: CampaignLifecycle): string {
  const slug = CAMPAIGN_MENU.find((m) => m.lifecycle === (lifecycle === 'rejected' ? 'draft' : lifecycle))?.slug ?? 'drafts';
  return `${CAMPAIGNS_BASE_PATH}/${slug}`;
}

/**
 * De manuelle overgange. Udkast kan godkendes eller afvises; en beslutning kan
 * trækkes tilbage til udkast, så længe kampagnen ikke er begyndt (en afvist
 * kampagne altid). En aktiv eller afsluttet kampagne har levet og kan ikke
 * gøres til udkast igen.
 */
export function allowedCampaignDecisions(lifecycle: CampaignLifecycle): CampaignDecision[] {
  switch (lifecycle) {
    case 'draft':
      return ['approved', 'rejected'];
    case 'rejected':
    case 'approved':
    case 'planned':
      return ['draft'];
    default:
      return [];
  }
}

// ---------------------------------------------------------------------------
// Kampagnens egne WP-kategorier ([D-473], Charlie 29-10-2026)
//
// Ud over tandhjulets kategorier for alle kampagner ([D-468], fx 5805 Tilbud)
// kan hver kampagne give sine tilbudsvarer egne WooCommerce-kategorier, fx
// Black Novembers tilbudssider: varer med primærkategorien Badeværelsesmøbler
// (172) får Tilbud Badeværelsesmøbler (6244). En regel uden primærkategori
// gælder alle kampagnens tilbudsvarer. Gemmes på campaigns.wp_category_rules.
// ---------------------------------------------------------------------------

export interface CampaignWpCategoryRule {
  /** WP-kategorien varen skal ligge i (eller under); null = alle kampagnens tilbudsvarer. */
  primaryCategoryId: number | null;
  /** WP-kategorien varen får, mens den er på tilbud i kampagnen. */
  categoryId: number;
}

export const CAMPAIGN_WP_CATEGORY_RULES_MAX = 30;

const wpId = (v: unknown): number | null => {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
  return typeof n === 'number' && Number.isInteger(n) && n > 0 ? n : null;
};

/** Rensede regler: gyldige id'er, ingen dubletter, højst CAMPAIGN_WP_CATEGORY_RULES_MAX. */
export function parseCampaignWpCategoryRules(value: unknown): CampaignWpCategoryRule[] {
  if (!Array.isArray(value)) return [];
  const out: CampaignWpCategoryRule[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    if (!raw || typeof raw !== 'object') continue;
    const r = raw as Record<string, unknown>;
    const categoryId = wpId(r.categoryId);
    if (categoryId == null) continue;
    const primaryCategoryId = r.primaryCategoryId == null || r.primaryCategoryId === '' ? null : wpId(r.primaryCategoryId);
    if (r.primaryCategoryId != null && r.primaryCategoryId !== '' && primaryCategoryId == null) continue;
    const key = `${primaryCategoryId ?? '*'}:${categoryId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ primaryCategoryId, categoryId });
    if (out.length >= CAMPAIGN_WP_CATEGORY_RULES_MAX) break;
  }
  return out;
}

/**
 * [D-507] Manuel styring af kampagnens egne WP-kategorier (Charlie 31-10):
 * 'on' = Tilknyt nu (også før kampagnen begynder, til den slutter), 'off' =
 * Fjern nu (indtil nogen tilknytter igen eller sætter tilbage til automatisk).
 * null = automatisk, mens kampagnen kører. Tandhjulets kategorier følger altid
 * kampagneperioden.
 */
export type CampaignWpCategoryOverride = 'on' | 'off';

export function parseCampaignWpCategoryOverride(value: unknown): CampaignWpCategoryOverride | null {
  return value === 'on' || value === 'off' ? value : null;
}

/** [D-507] Gælder kampagnens egne kategorier i dag? */
export function campaignOwnWpCategoriesActive(lifecycle: CampaignLifecycle, override: CampaignWpCategoryOverride | null): boolean {
  if (override === 'off') return false;
  if (override === 'on') return lifecycle === 'active' || lifecycle === 'planned' || lifecycle === 'approved';
  return lifecycle === 'active';
}

/**
 * Hvilke af reglernes primærkategorier hører produktet til? Står en af dem
 * DIREKTE på produktet i WP, gælder kun de direkte (BC's PRIMÆRKATEGORI sætter
 * dem; målt 29-10: 217 af 217 Black November-varer). Står ingen direkte (fx et
 * restparti med Outlet som primær, men underkategorien Badekar under Sanitet),
 * gælder de primærkategorier, produktets kategorier ligger under i WP's træ.
 */
export function matchPrimaryCategories(
  productCategoryIds: readonly number[],
  primaryIds: readonly number[],
  parentOf: (id: number) => number | undefined,
): number[] {
  const primaries = new Set(primaryIds);
  const direct = productCategoryIds.filter((c) => primaries.has(c));
  if (direct.length > 0) return [...new Set(direct)];
  const hit = new Set<number>();
  for (const c of productCategoryIds) {
    let cur: number | undefined = parentOf(c);
    for (let i = 0; cur && i < 20; i++, cur = parentOf(cur)) {
      if (primaries.has(cur)) hit.add(cur);
    }
  }
  return [...hit];
}

/**
 * Forslag ud fra navnene: en WP-kategori der hedder "Tilbud X" parres med
 * kategorien "X" (fx Tilbud Armaturer 6239 med Armaturer 141). Kategorier der
 * allerede har en regel, springes over.
 */
export function suggestCampaignWpCategoryRules(
  categories: ReadonlyArray<{ id: number; name: string }>,
  existing: readonly CampaignWpCategoryRule[] = [],
  prefix = 'Tilbud ',
): CampaignWpCategoryRule[] {
  const norm = (s: string): string => s.trim().toLocaleLowerCase('da-DK');
  const p = prefix.toLocaleLowerCase('da-DK');
  const byName = new Map<string, number>();
  for (const c of categories) if (!norm(c.name).startsWith(p)) byName.set(norm(c.name), c.id);
  const taken = new Set(existing.map((r) => r.categoryId));
  const out: CampaignWpCategoryRule[] = [];
  for (const c of categories) {
    if (!norm(c.name).startsWith(p) || taken.has(c.id)) continue;
    const primary = byName.get(norm(c.name.trim().slice(prefix.length)));
    if (primary != null) out.push({ primaryCategoryId: primary, categoryId: c.id });
  }
  return out;
}
